import { Conditions, GroupCondition } from '@/api/enums';
import { PhishingLanguagesEnum } from '@/api/enums/phishing-languages.enum';
import { equal, greater, group, handleAxiosError } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { client } from '@/client';
import { Account } from '@/client/accounts';
import {
  Campaign,
  CampaignStatusesEnum,
  CampaignSyncFailedPayload,
  SimulationPlanData,
  SimulationSchedulerItem,
  SimulationWithAssistancePlanData,
} from '@/client/campaigns';
import {
  Actions as AclActions,
  Subjects as AclSubjects,
  SystemRoles,
} from '@/client/users';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { DialogContext } from '@/common/context';
import {
  TargetEntities,
  TargetEntitiesRequest,
  TranslationFunctionType,
} from '@/common/types';
import { BlockEmailsBanner } from '@/components/block-emails';
import {
  AutoScheduleHeader,
  AutoScheduleOperation,
  InfoCards,
  SimulationScheduler,
  WhitelistingBanner,
} from '@/components/campaigns/simulations';
import { AddSimulationsCard } from '@/components/campaigns/simulations/AddSimulationsCard';
import {
  ScheduleUsersWarningModal,
  WhitelistingModal,
} from '@/components/campaigns/simulations/modals';
import { PlanSimulationModal } from '@/components/campaigns/simulations/modals/PlanSimulationModal';
import { PlanWithAssistanceModal } from '@/components/campaigns/simulations/modals/PlanWithAssistanceModal';
import { SendTestEmailDialog } from '@/components/campaigns/simulations/modals/SendTestEmailDialog';
import { CourseProcessing } from '@/components/courses/CourseProcessing';
import { useNotifications } from '@/hooks/notifications.hook';
import { useAccount } from '@/hooks/query';
import { featuredQueryKey } from '@/hooks/query/campaign-templates.hook';
import {
  placeholderPreviewQueryKey,
  useAllCampaigns,
  useAutoPhishingSummary,
  useCampaigns,
  useCreateAutoPhishingCampaign,
  useCreateAutoPhishingCampaigns,
  useDeleteCampaign,
  useDeleteLanguageGroup,
  useLaunchAutoPhishingCampaigns,
  useUpdateCampaign,
  useUpdateLanguageGroup,
} from '@/hooks/query/campaigns.hook';
import { useAppSelector } from '@/hooks/store';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useLanguageFailureToasts } from '@/hooks/useLanguageFailureToasts';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/hooks/useToast';
import { EmptyAutoPhishingPage } from '@/pages/campaigns/EmptyAutoPhishingPage';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { DialogType } from '@/system-settings/enums';
import { FlexContainer } from '@/ui';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { AppButton } from '@/ui/buttons';
import {
  getIsActivePhishingStatus,
  getPhishingLanguageCode,
  getPhishingSimulationStatusColors,
  getPlaceholderSendByDate,
  isDemoRestrictedMode,
  mapLanguageToPhishingLanguage,
  showBlockEmailsMessage,
} from '@/utils/helpers';
import { PostHogFeatureFlags } from '@/utils/posthog';
import { getTargetsAndExcludes } from '@/utils/targets';
import { AxiosError } from 'axios';
import debounce from 'lodash/debounce';
import moment from 'moment';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tooltip } from 'primereact/tooltip';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';

const StyledDescription = styled.p`
  color: var(--black-main);
  font-size: var(--small-font-size);
  margin: 0;
  line-height: 1.4;
`;

const DemoContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  min-width: 0;
`;

const SchedulerWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const AutoScheduleSection = styled.div<{ $hidden: boolean }>`
  display: ${({ $hidden }) => ($hidden ? 'none' : 'contents')};
`;

const AutoScheduleLoadingPanel = styled.div`
  background-color: var(--beige-main);
  border-radius: var(--semi-medium-border-radius);
  padding: var(--default-padding) 0 var(--big-padding);
`;

const StyledScheduler = styled.div`
  min-width: 0;

  .simulation-scheduler {
    border-radius: 0 0 var(--semi-medium-border-radius)
      var(--semi-medium-border-radius);
  }
`;

const Actions = styled(FlexContainer)`
  justify-content: flex-start;
  margin-top: var(--default-padding);
  gap: var(--small-padding);

  .info-message {
    i {
      color: var(--orange-main);
      font-size: var(--medium-font-size);
    }

    p {
      font-size: var(--xsmall-font-size);
      line-height: 150%;
      font-weight: 500;
      margin-left: var(--small-padding);
    }
  }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: var(--default-padding);
  color: var(--white-main);
  animation: fadeIn 0.2s ease-in-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  .loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--default-padding);
    padding: 40px 60px;
  }

  .loading-text {
    font-size: 20px;
    font-weight: 500;
    text-align: center;
    margin: 0;
  }

  .loading-dots {
    display: inline-block;
    font-weight: 500;

    span {
      display: inline-block;
      animation: dotFade 1.4s ease-in-out infinite;
      opacity: 0.4;

      &:nth-child(1) {
        animation-delay: 0s;
      }

      &:nth-child(2) {
        animation-delay: 0.2s;
      }

      &:nth-child(3) {
        animation-delay: 0.4s;
      }
    }

    @keyframes dotFade {
      0%,
      60%,
      100% {
        opacity: 0.4;
      }
      30% {
        opacity: 1;
      }
    }
  }

  .loading-subtext {
    font-size: var(--small-font-size);
    font-weight: 400;
    opacity: 0.8;
    text-align: center;
    margin: 0;
  }

  svg circle {
    stroke: var(--green-color) !important;
  }
`;

const toSchedulerItem = (
  campaign: Campaign,
  canDeleteCampaigns: boolean,
  now: Date,
  isTemplateLevelView = false,
): SimulationSchedulerItem & Pick<Campaign, 'targets'> => {
  const isDraft = campaign.status === CampaignStatusesEnum.DRAFT;
  const isPlaceholder = campaign.status === CampaignStatusesEnum.PLACEHOLDER;
  const startDate = campaign.launchDate ? new Date(campaign.launchDate) : null;
  const sendByDate = campaign.sendByDate ? new Date(campaign.sendByDate) : null;
  const endDate =
    isPlaceholder && startDate
      ? getPlaceholderSendByDate(startDate, sendByDate)
      : sendByDate || startDate;
  const isDateInvalid = isDraft && (!startDate || startDate < now);

  return {
    id: campaign.id,
    name:
      (isTemplateLevelView && campaign.masterTemplateName) ||
      campaign.name ||
      campaign.meta?.cloneOfName,
    startDate: isDateInvalid ? null : startDate,
    endDate: isDateInvalid ? null : endDate,
    targetGroup: campaign.targets,
    excludes: campaign.excludes,
    targets: campaign.targets,
    color: getPhishingSimulationStatusColors(campaign.status),
    disabled: !isDraft && !isPlaceholder,
    canDelete:
      canDeleteCampaigns &&
      (campaign.status === CampaignStatusesEnum.DRAFT ||
        campaign.status === CampaignStatusesEnum.SCHEDULED ||
        campaign.status === CampaignStatusesEnum.PENDING_LAUNCH ||
        campaign.status === CampaignStatusesEnum.ACTIVE),
    campaignTemplateId: campaign.meta?.cloneOf,
    difficulty: campaign.meta?.cloneOfDifficulty,
    phishingSign: campaign.meta?.phishingSign,
    status: campaign.status,
    targetUsersCount: campaign.summary?.total || campaign.targetUsersCount || 0,
    emailTemplateId: campaign.emailTemplate?.id,
    sendingProfileId: campaign.sendingProfile?.id,
    landingPageId: campaign.landingPage?.id,
    language: campaign.language
      ? mapLanguageToPhishingLanguage(campaign.language) ?? undefined
      : undefined,
  };
};

const getSendLanguage = (campaign: Campaign) =>
  campaign.language ? mapLanguageToPhishingLanguage(campaign.language) : null;

const toLanguageGroupItem = (
  sends: Campaign[],
  canDeleteCampaigns: boolean,
  now: Date,
  isTemplateLevelView: boolean,
): SimulationSchedulerItem & Pick<Campaign, 'targets'> => {
  const [firstSend] = sends;
  const fallbackSend = sends.find(
    (send) => send.languageFilterMode === 'fallback',
  );
  const otherSends = sends
    .filter((send) => send !== fallbackSend)
    .map((send) => ({ send, language: getSendLanguage(send) }))
    .filter(
      (entry): entry is { send: Campaign; language: PhishingLanguagesEnum } =>
        !!entry.language,
    )
    .sort((a, b) =>
      getPhishingLanguageCode(a.language).localeCompare(
        getPhishingLanguageCode(b.language),
      ),
    );
  const fallbackLanguage = fallbackSend ? getSendLanguage(fallbackSend) : null;
  const orderedSends = [
    ...(fallbackSend && fallbackLanguage
      ? [{ send: fallbackSend, language: fallbackLanguage }]
      : []),
    ...otherSends,
  ];
  const testSend = fallbackSend ?? orderedSends[0]?.send ?? firstSend;
  const firstItem = toSchedulerItem(
    firstSend,
    canDeleteCampaigns,
    now,
    isTemplateLevelView,
  );

  return {
    ...firstItem,
    // A failed group can only be cleaned up through the group endpoint, so it
    // is only deletable when it is shown as a group.
    canDelete:
      firstItem.canDelete ||
      (canDeleteCampaigns &&
        firstSend.status === CampaignStatusesEnum.FAILED_LAUNCH),
    targetUsersCount: sends.reduce(
      (total, send) =>
        total + (send.summary?.total || send.targetUsersCount || 0),
      0,
    ),
    emailTemplateId: testSend.emailTemplate?.id,
    sendingProfileId: testSend.sendingProfile?.id,
    landingPageId: testSend.landingPage?.id,
    languageGroupId: firstSend.languageGroupId ?? undefined,
    sendIds: sends.map((send) => send.id),
    languages: orderedSends.map(({ language }) => language),
    testSendId: testSend.id,
    testSends: orderedSends.map(({ send, language }) => ({
      campaignId: send.id,
      language,
      isFallback: send === fallbackSend,
      emailTemplateId: send.emailTemplate?.id,
      sendingProfileId: send.sendingProfile?.id,
      landingPageId: send.landingPage?.id,
    })),
  };
};

const toSchedulerItems = (
  campaigns: Campaign[],
  canDeleteCampaigns: boolean,
  now: Date,
  isTemplateLevelView: boolean,
  groupByLanguage: boolean,
): (SimulationSchedulerItem & Pick<Campaign, 'targets'>)[] => {
  const sendsByGroup = new Map<string, Campaign[]>();

  if (groupByLanguage) {
    campaigns.forEach((campaign) => {
      if (!campaign.languageGroupId) return;
      sendsByGroup.set(campaign.languageGroupId, [
        ...(sendsByGroup.get(campaign.languageGroupId) ?? []),
        campaign,
      ]);
    });
  }

  return campaigns.flatMap((campaign) => {
    const sends = campaign.languageGroupId
      ? sendsByGroup.get(campaign.languageGroupId)
      : undefined;

    if (!sends) {
      return [
        toSchedulerItem(campaign, canDeleteCampaigns, now, isTemplateLevelView),
      ];
    }

    return sends[0] === campaign
      ? [
          toLanguageGroupItem(
            sends,
            canDeleteCampaigns,
            now,
            isTemplateLevelView,
          ),
        ]
      : [];
  });
};

const getSchedulerItemKey = (item: SimulationSchedulerItem) =>
  item.languageGroupId ?? item.id;

const toSortedIds = (ids: string[] = []) => [...ids].sort().join(',');

const haveTargetsChanged = (
  stored: TargetEntities | undefined,
  submitted: TargetEntitiesRequest,
) =>
  (['branches', 'groups'] as const).some(
    (type) =>
      toSortedIds(stored?.[type]?.map(({ id }) => id)) !==
      toSortedIds(submitted[type]),
  );

const pathItems = (t: TranslationFunctionType, account: Account) => [
  {
    label: account?.name,
    url: !account?.isSystem
      ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
      : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
    template: AppBreadCrumbTemplate,
  },
  {
    label: t('phishing.simulations.title'),
    url: RedirectPaths[RedirectPathsEnum.PHISHING_SIMULATIONS](),
    template: AppBreadCrumbTemplate,
  },
];

export const PhishingSimulationsPage: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { setDialogData } = useContext(DialogContext);
  const currentAccount = useAppSelector(selectCurrentAccount);
  const hasBlockEmailsBanner =
    showBlockEmailsMessage(currentAccount) &&
    !!currentAccount?.settings?.phishingFollowUpEnabled;
  const currentUser = useAppSelector(selectCurrentUser);
  const isDemoAccount = isDemoRestrictedMode(currentAccount, currentUser);
  const { account: freshAccount } = useAccount({
    accountId: currentAccount?.id,
    enabled: !!currentAccount?.id && !currentAccount?.isSystem,
  });
  const isPhishingSenderWhitelisted =
    freshAccount?.isPhishingSenderWhitelisted ??
    currentAccount?.isPhishingSenderWhitelisted;
  const [campaingsMapped, setCampaingsMapped] = useState<
    SimulationSchedulerItem[]
  >([]);
  const deletedLanguageGroupIdsRef = useRef(new Set<string>());
  const [initialLoad, setInitialLoad] = useState(true);
  const [isGeneratingSchedule, setIsGeneratingSchedule] = useState(false);
  const [isLaunchingSimulations, setIsLaunchingSimulations] = useState(false);
  const [isAutoScheduleLoading, setIsAutoScheduleLoading] = useState(false);
  const [autoScheduleOperation, setAutoScheduleOperation] =
    useState<AutoScheduleOperation>('enable');
  const { canUseFeature, accountCanUseFeature } = useFeatureFlag();
  const { can, isRestrictedByRole } = usePermission();
  const canDeleteCampaigns = can(AclActions.DELETE, AclSubjects.CAMPAIGNS);
  const isCampaignEditRestricted = isRestrictedByRole(
    AclActions.UPDATE,
    AclSubjects.CAMPAIGNS,
  );
  const hasAutoSchedulerFlag = canUseFeature(
    PostHogFeatureFlags.AUTO_PHISHING_SCHEDULER,
  );
  const isAutoScheduleEnabled =
    hasAutoSchedulerFlag && !!currentAccount?.enableAutoPhishingMode;
  // Prefer the freshly loaded account so grouping follows the same flags as
  // the language failure notices.
  const featureFlagAbility =
    (freshAccount ?? currentAccount)?.featureFlagAbility ?? [];
  const isTemplateLevelView = accountCanUseFeature(
    PostHogFeatureFlags.PHISHING_TEMPLATE_LEVEL_VIEW,
    featureFlagAbility,
  );
  const isLanguageTargetingActive =
    accountCanUseFeature(
      PostHogFeatureFlags.SMART_LANGUAGE_TARGETING,
      featureFlagAbility,
    ) && isTemplateLevelView;

  const currentDate = React.useMemo(
    () =>
      moment()
        .hour(0)
        .minute(0)
        .second(0)
        .millisecond(0)
        .toDate()
        .toISOString(),
    [],
  );
  const filters = useMemo(
    () => [
      {
        field: 'account',
        condition: Conditions.EQUAL,
        value: (currentAccount as Account)?.id,
      },
      {
        field: 'status',
        condition: Conditions.NOT_EQUAL,
        value: CampaignStatusesEnum.COMPLETED,
      },
      equal('isAutoPhishing', true),
      group(GroupCondition.OR, [
        greater('sendByDate', currentDate, true),
        greater('launchDate', currentDate, true),
        equal('status', CampaignStatusesEnum.DRAFT),
        equal('status', CampaignStatusesEnum.SCHEDULED),
        equal('status', CampaignStatusesEnum.ACTIVE),
      ]),
    ],
    [currentAccount?.id, currentDate],
  );

  const {
    campaigns,
    refetch: refetchCampaigns,
    isLoading,
  } = useAllCampaigns({
    withTargets: 1,
    withDifficultyInMeta: 1,
    take: 200,
    enabled: !currentAccount?.isSystem,
    filters,
    sort: ['launchDate,asc'],
  });

  const {
    autoPhishingSummary,
    refetch: refetchAutoPhishingSummary,
    isLoading: isLoadingAutoPhishingSummary,
  } = useAutoPhishingSummary(currentAccount?.id, {
    enabled: !!currentAccount?.id,
    filters,
  });

  const {
    isLoading: isLoadingSimplifiedScreenCheck,
    campaigns: simplifiedScreenCheck,
    refetch: refetchSimplifiedScreenCheck,
  } = useCampaigns({
    take: 0,
    skip: 0,
    enabled: !currentAccount?.isSystem,
    filters: [
      equal('account', (currentAccount as Account)?.id),
      equal('isAutoPhishing', true),
    ],
  });

  useEffect(() => {
    if (!isLoading && initialLoad && !isLoadingAutoPhishingSummary) {
      setInitialLoad(false);
    }
  }, [isLoading, isLoadingAutoPhishingSummary, initialLoad]);

  useEffect(() => {
    if (campaigns) {
      const mapped = toSchedulerItems(
        campaigns.result,
        canDeleteCampaigns,
        new Date(),
        isTemplateLevelView,
        isLanguageTargetingActive,
      ).filter(
        (item) =>
          !item.languageGroupId ||
          !deletedLanguageGroupIdsRef.current.has(item.languageGroupId),
      );

      mapped.sort((a, b) => {
        const aDateMissing =
          a.status === CampaignStatusesEnum.DRAFT && !a.startDate;
        const bDateMissing =
          b.status === CampaignStatusesEnum.DRAFT && !b.startDate;
        if (aDateMissing && !bDateMissing) return 1;
        if (!aDateMissing && bDateMissing) return -1;
        return 0;
      });

      setCampaingsMapped(mapped);
    }
  }, [
    campaigns,
    canDeleteCampaigns,
    isTemplateLevelView,
    isLanguageTargetingActive,
  ]);

  useLanguageFailureToasts(freshAccount);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] =
    useState<SimulationSchedulerItem | null>(null);
  const [isWhitelistingModalOpen, setIsWhitelistingModalOpen] = useState(false);
  const [isPlanWithAssistanceModalOpen, setIsPlanWithAssistanceModalOpen] =
    useState(false);
  const [isUsersWarningModalOpen, setIsUsersWarningModalOpen] = useState(false);
  const [activeUsersCount, setActiveUsersCount] = useState(0);
  const [testEmailSimulation, setTestEmailSimulation] =
    useState<SimulationSchedulerItem | null>(null);

  const canDeleteCampaignsRef = useRef(canDeleteCampaigns);
  canDeleteCampaignsRef.current = canDeleteCampaigns;
  const isTemplateLevelViewRef = useRef(isTemplateLevelView);
  isTemplateLevelViewRef.current = isTemplateLevelView;
  const isLanguageTargetingActiveRef = useRef(isLanguageTargetingActive);
  isLanguageTargetingActiveRef.current = isLanguageTargetingActive;
  const currentAccountIdRef = useRef(currentAccount?.id);
  currentAccountIdRef.current = currentAccount?.id;

  const restoreSavedDates = async (task: SimulationSchedulerItem) => {
    const { data } = await refetchCampaigns();
    const taskKey = getSchedulerItemKey(task);
    const saved = toSchedulerItems(
      data?.result ?? [],
      canDeleteCampaignsRef.current,
      new Date(),
      isTemplateLevelViewRef.current,
      isLanguageTargetingActiveRef.current,
    ).find((item) => getSchedulerItemKey(item) === taskKey);

    if (!saved) return;

    setCampaingsMapped((prev) =>
      prev.map((campaign) =>
        getSchedulerItemKey(campaign) === taskKey ? saved : campaign,
      ),
    );
  };

  const refreshPlaceholderPreview = () =>
    queryClient.invalidateQueries([placeholderPreviewQueryKey], undefined, {
      cancelRefetch: true,
    });

  const refreshPlanner = async () => {
    refreshPlaceholderPreview();
    await Promise.all([
      refetchCampaigns(),
      refetchAutoPhishingSummary(),
      refetchSimplifiedScreenCheck(),
    ]);
  };

  const pendingUpdateIdRef = useRef<string | null>(null);
  const debouncedUpdateRef = React.useRef(
    debounce(async (task: SimulationSchedulerItem) => {
      pendingUpdateIdRef.current = null;

      try {
        const accountId = currentAccountIdRef.current;

        if (task.languageGroupId && accountId) {
          await updateLanguageGroup.updateLanguageGroup(
            accountId,
            task.languageGroupId,
            {
              launchDate: task.startDate ?? undefined,
              sendByDate: task.endDate ?? undefined,
            },
          );
        } else {
          await updateCampaign.update({
            campaignId: task.id,
            updates: {
              launchDate: task.startDate ?? undefined,
              sendByDate: task.endDate ?? undefined,
            },
          });
        }

        if (task.status === CampaignStatusesEnum.PLACEHOLDER) {
          refreshPlaceholderPreview();
        }
      } catch (e) {
        handleAxiosError(e as Error | AxiosError, toast);
        await restoreSavedDates(task);
      }
    }, 600),
  );

  useEffect(() => {
    const debouncedUpdate = debouncedUpdateRef.current;

    return () => {
      debouncedUpdate.flush();
    };
  }, []);

  const handleCampaignChange = (updatedCampaign: SimulationSchedulerItem) => {
    const updatedKey = getSchedulerItemKey(updatedCampaign);

    setCampaingsMapped((prev) =>
      prev.map((campaign) =>
        getSchedulerItemKey(campaign) === updatedKey
          ? updatedCampaign
          : campaign,
      ),
    );

    if (
      pendingUpdateIdRef.current &&
      pendingUpdateIdRef.current !== updatedKey
    ) {
      debouncedUpdateRef.current.flush();
    }

    pendingUpdateIdRef.current = updatedKey;
    debouncedUpdateRef.current(updatedCampaign);
  };

  const handleCampaignEdit = (task: SimulationSchedulerItem) => {
    setSelectedTask(task);
    setIsModalOpen(true);
  };

  const [deletingCampaignId, setDeletingCampaignId] = useState<string | null>(
    null,
  );

  const {
    lastMessage: lastMessageDeletion,
    setNotificationParam: setIdForDeletion,
    notificationParam: notificationForDeletion,
    isConnected: isConnectedForDeletion,
  } = useNotifications(client.campaigns.campaignsNotifyUrl);

  useEffect(() => {
    if (!notificationForDeletion) return;
    handleNotifyCampaignDelete(lastMessageDeletion);
  }, [JSON.stringify(lastMessageDeletion)]);

  const handleNotifyCampaignDelete = async (messages: any) => {
    if (
      !messages?.data?.event ||
      !['campaign.delete.finished', 'campaign.delete.error'].includes(
        messages?.data?.event,
      )
    )
      return;

    if (messages?.data?.event === 'campaign.delete.finished') {
      setDeletingCampaignId(null);
      await refreshPlanner();
      toast?.success(t('toast.success'), t('campaign.deleted'));
    }

    if (messages?.data?.event === 'campaign.delete.error') {
      setDeletingCampaignId(null);
      toast?.error(t('toast.error'), t('campaign.not.deleted'), 5000);
    }

    setIdForDeletion(undefined);
  };

  const deleteCampaign = useDeleteCampaign();
  const deleteLanguageGroup = useDeleteLanguageGroup();
  const handleDeleteCampaign = async (
    campaignId: string,
    languageGroupId?: string,
  ) => {
    try {
      const accountId = (currentAccount as Account)?.id;

      if (languageGroupId && accountId) {
        await deleteLanguageGroup.deleteLanguageGroup(
          accountId,
          languageGroupId,
        );
        deletedLanguageGroupIdsRef.current.add(languageGroupId);
      } else {
        await deleteCampaign.delete(campaignId);
      }
      await refetchSimplifiedScreenCheck();
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
      setDeletingCampaignId(null);
    }
  };

  const handleCampaignDelete = (campaignId: string) => {
    const languageGroupId = campaingsMapped.find(
      (campaign) => campaign.id === campaignId,
    )?.languageGroupId;

    setDialogData({
      show: true,
      type: DialogType.CONFIRMATION,
      header: t('campaign.delete'),
      message: t('campaign.delete.confirm'),
      closeImmediatelyAfterAccept: true,
      onAccept: async () => {
        setIdForDeletion(campaignId);
        setDeletingCampaignId(campaignId);

        // Wait for a notify connection to establish
        await new Promise((resolve) => {
          const intervalId = setInterval(() => {
            if (isConnectedForDeletion.current) {
              clearInterval(intervalId);
              resolve(true);
            }
          }, 100);
        });
        try {
          if (isConnectedForDeletion.current) {
            await handleDeleteCampaign(campaignId, languageGroupId);
            setCampaingsMapped((prev) =>
              prev.filter((campaign) => campaign.id !== campaignId),
            );
          }
        } catch (e) {
          handleAxiosError(e as Error | AxiosError, toast);
        }
      },
    });
  };

  const handleSingleSimulation = () => {
    setIsModalOpen(true);
  };

  const handlePlanWithAssistance = () => {
    setIsPlanWithAssistanceModalOpen(true);
  };

  const createAutoPhishing = useCreateAutoPhishingCampaign();
  const updateCampaign = useUpdateCampaign();
  const updateLanguageGroup = useUpdateLanguageGroup();
  const handlePlanSimulation = async (data: SimulationPlanData) => {
    setIsModalOpen(false);

    if (data.id) {
      try {
        const { targets, excludes } = getTargetsAndExcludes(
          data.targetGroup ?? [],
          data.excludes ?? [],
        );
        const updates = {
          launchDate: data.startDate as Date,
          sendByDate: data.endDate as Date,
          targets,
          // The API replaces the stored exclusions with any it is sent
          ...(data.excludes && { excludes }),
        };
        const editedItem = campaingsMapped.find(
          (campaign) => campaign.id === data.id,
        );
        const languageGroupId = editedItem?.languageGroupId;
        const accountId = (currentAccount as Account)?.id;

        if (languageGroupId && accountId) {
          await updateLanguageGroup.updateLanguageGroup(
            accountId,
            languageGroupId,
            haveTargetsChanged(editedItem?.targetGroup, targets) ||
              data.excludes
              ? updates
              : {
                  launchDate: updates.launchDate,
                  sendByDate: updates.sendByDate,
                },
          );
        } else {
          await updateCampaign.update({ campaignId: data.id, updates });
        }
        await refreshPlanner();
        toast?.success(t('toast.success'), t('phishing.simulation.updated'));
        setSelectedTask(null);
      } catch (e) {
        handleAxiosError(e as Error | AxiosError, toast);
      }
    } else {
      try {
        const accountId = (currentAccount as Account)?.id;
        if (!accountId || !data.campaignTemplateId) return;

        await createAutoPhishing.create(accountId, {
          campaignTemplateId: data.campaignTemplateId,
          launchDate: (data.startDate as Date).toISOString(),
          sendByDate: (data.endDate as Date).toISOString(),
          ...getTargetsAndExcludes(data.targetGroup ?? [], data.excludes ?? []),
        });

        await refreshPlanner();
        toast?.success(t('toast.success'), t('phishing.simulation.created'));
      } catch (e) {
        handleAxiosError(e as Error | AxiosError, toast);
      }
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTask(null);
  };

  const handleStartWhitelisting = () => {
    setIsWhitelistingModalOpen(true);
  };

  const handleCloseWhitelistingModal = () => {
    setIsWhitelistingModalOpen(false);
  };

  const createAutoPhishingCampaigns = useCreateAutoPhishingCampaigns();

  const {
    lastMessage: lastMessageAiSchedule,
    setNotificationParam: setJobIdForAiSchedule,
    notificationParam: jobIdForAiSchedule,
    isConnected: isConnectedForAiSchedule,
  } = useNotifications(client.campaigns.autoPhishingCampaignsNotifyUrl);

  useEffect(() => {
    if (!jobIdForAiSchedule) return;
    handleNotifyAiScheduleCreate(lastMessageAiSchedule);
  }, [JSON.stringify(lastMessageAiSchedule)]);

  const handleNotifyAiScheduleCreate = async (messages: any) => {
    if (
      !messages?.data?.event ||
      ![
        'auto-phishing-campaigns.create.finished',
        'auto-phishing-campaigns.create.failed',
      ].includes(messages?.data?.event)
    )
      return;

    if (messages?.data?.event === 'auto-phishing-campaigns.create.finished') {
      queryClient.invalidateQueries([featuredQueryKey, currentAccount?.id]);
      await refreshPlanner();
      toast?.success(t('toast.success'), t('phishing.simulations.created'));
    }

    if (messages?.data?.event === 'auto-phishing-campaigns.create.failed') {
      toast?.error(
        t('toast.error'),
        messages?.data?.error || t('phishing.simulations.failedToCreate'),
      );
    }

    setIsGeneratingSchedule(false);
    setIsPlanWithAssistanceModalOpen(false);
    setJobIdForAiSchedule(undefined);
  };

  const handleCreateSchedule = async (
    data: SimulationWithAssistancePlanData,
  ) => {
    const accountId = (currentAccount as Account)?.id;
    if (!accountId) return;

    setIsGeneratingSchedule(true);

    try {
      const response = await createAutoPhishingCampaigns.create(accountId, {
        period: data.timePeriod,
        campaignsCount: data.numberOfSimulations,
      });

      const jobId = response?.message;

      if (jobId) {
        setJobIdForAiSchedule(jobId);

        // Wait for a notify connection to establish
        await new Promise((resolve) => {
          const intervalId = setInterval(() => {
            if (isConnectedForAiSchedule.current) {
              clearInterval(intervalId);
              resolve(true);
            }
          }, 100);
        });
      } else {
        setIsGeneratingSchedule(false);
        setIsPlanWithAssistanceModalOpen(false);
      }
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
      setIsGeneratingSchedule(false);
      setIsPlanWithAssistanceModalOpen(false);
    }
  };
  const handleClosePlanWithAssistanceModal = () => {
    setIsPlanWithAssistanceModalOpen(false);
  };

  const {
    lastMessage: lastMessageSchedule,
    setNotificationParam: setAccountIdForSchedule,
    notificationParam: notificationForSchedule,
    isConnected: isConnectedForSchedule,
  } = useNotifications(client.campaigns.campaingsAccountNotifyUrl);

  const syncedCampaingsRef = useRef(new Set<string>());
  const launchingSendsCountRef = useRef(0);
  const failedToLaunchCampaignsRef = useRef<string[]>([]);
  const failedSendIdsRef = useRef(new Set<string>());
  const shownLanguageGroupFailuresRef = useRef(new Set<string>());

  useEffect(() => {
    if (!notificationForSchedule) return;
    handleNotifyCampaignSchedule(lastMessageSchedule);
  }, [JSON.stringify(lastMessageSchedule)]);

  const draftCampaigns = campaingsMapped.filter(
    (c) => c.status === CampaignStatusesEnum.DRAFT,
  );

  const handleLanguageGroupFailure = ({
    languageGroupId,
    name,
  }: CampaignSyncFailedPayload) => {
    if (
      !languageGroupId ||
      shownLanguageGroupFailuresRef.current.has(languageGroupId)
    )
      return;

    shownLanguageGroupFailuresRef.current.add(languageGroupId);
    toast?.error(
      t('phishing.simulations.languageGroupFailed.title'),
      t('phishing.simulations.languageGroupFailed.body', { name }),
    );
  };

  const handleNotifyCampaignSchedule = async (messages: any) => {
    if (
      !messages?.data?.event ||
      ![
        'campaign.create.sync.finished',
        'campaign.create.sync.failed',
      ].includes(messages?.data?.event)
    )
      return;

    const payload = messages?.data?.payload;

    if (messages?.data?.event === 'campaign.create.sync.finished') {
      syncedCampaingsRef.current.add(payload?.id);
    }

    if (messages?.data?.event === 'campaign.create.sync.failed') {
      syncedCampaingsRef.current.add(payload?.id);
      failedSendIdsRef.current.add(payload?.id);

      if (payload?.languageGroupId) {
        handleLanguageGroupFailure(payload);
      } else if (!failedToLaunchCampaignsRef.current.includes(payload?.id)) {
        failedToLaunchCampaignsRef.current.push(payload?.id);
      }
    }

    if (syncedCampaingsRef.current.size === launchingSendsCountRef.current) {
      await refreshPlanner();

      if (failedToLaunchCampaignsRef.current.length > 0) {
        toast?.error(
          t('toast.error'),
          t('phishing.simulations.failedToLaunch', {
            count: failedToLaunchCampaignsRef.current.length,
          }),
        );
      } else if (
        [...syncedCampaingsRef.current].some(
          (id) => !failedSendIdsRef.current.has(id),
        )
      ) {
        toast?.success(t('toast.success'), t('phishing.simulations.scheduled'));
      }

      setIsLaunchingSimulations(false);

      setAccountIdForSchedule(undefined);
      syncedCampaingsRef.current = new Set();
      failedToLaunchCampaignsRef.current = [];
      failedSendIdsRef.current = new Set();
    }
  };

  const launchAutoPhishing = useLaunchAutoPhishingCampaigns();
  const launchSimulations = async () => {
    const accountId = (currentAccount as Account)?.id;
    if (!accountId) return;

    const draftCampaignIds = (campaingsMapped || [])
      .filter((c) => c.status === CampaignStatusesEnum.DRAFT)
      .flatMap((c) => c.sendIds ?? [c.id]);

    syncedCampaingsRef.current = new Set();
    failedToLaunchCampaignsRef.current = [];
    failedSendIdsRef.current = new Set();
    launchingSendsCountRef.current = draftCampaignIds.length;
    setIsLaunchingSimulations(true);
    setAccountIdForSchedule(accountId);

    // Wait for a notify connection to establish
    await new Promise((resolve) => {
      const intervalId = setInterval(() => {
        if (isConnectedForSchedule.current) {
          clearInterval(intervalId);
          resolve(true);
        }
      }, 100);
    });

    try {
      if (isConnectedForSchedule.current) {
        await launchAutoPhishing.launch(accountId, draftCampaignIds);
      }
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
      setIsLaunchingSimulations(false);
    }
  };

  const handleScheduleSimulations = async () => {
    const accountId = (currentAccount as Account)?.id;
    if (!accountId) return;

    try {
      const usersResponse = await client.users.getAccountUsers(
        {
          take: 0,
          skip: 0,
          filters: [equal('active', true)],
        },
        accountId,
      );

      setActiveUsersCount(usersResponse.count);

      if (
        !autoPhishingSummary?.scheduledCount &&
        !autoPhishingSummary?.activeCount
      ) {
        setIsUsersWarningModalOpen(true);
        return;
      }
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
      return;
    }

    launchSimulations();
  };

  const hasDateMissingDraftCampaigns = useMemo(() => {
    return draftCampaigns.some((campaign) => !campaign.startDate);
  }, [draftCampaigns]);

  const hasSimplifiedPhishingPlanningScreenFlag = canUseFeature(
    PostHogFeatureFlags.SIMPLIFIED_PHISHING_PLANNING_SCREEN,
  );

  if (
    initialLoad &&
    (isLoading ||
      isLoadingAutoPhishingSummary ||
      isLoadingSimplifiedScreenCheck)
  ) {
    return (
      <FlexContainer direction="column" className="mt-5" height="100%">
        <ProgressSpinner />
        <h3>{t('phishing.simulations.loading')}</h3>
      </FlexContainer>
    );
  }

  return (
    <FlexContainer
      direction="column"
      align="flex-start"
      justify="flex-start"
      style={{ minHeight: '100%' }}
    >
      {hasBlockEmailsBanner && <BlockEmailsBanner />}
      {!simplifiedScreenCheck?.count &&
      !getIsActivePhishingStatus(currentAccount) &&
      hasSimplifiedPhishingPlanningScreenFlag &&
      [SystemRoles.ADMIN, SystemRoles.MULTIPLE_ACCOUNTS_ADMIN].includes(
        currentUser?.role.code as SystemRoles,
      ) ? (
        <EmptyAutoPhishingPage
          handlePlanSimulation={handlePlanSimulation}
          selectedTask={selectedTask}
          setSelectedTask={setSelectedTask}
        />
      ) : (
        <>
          <AppBreadCrumb model={pathItems(t, currentAccount as Account)} />
          <h1 className="mb-0">{t('phishing.simulations.title')}</h1>
          <StyledDescription className="mb-4">
            {t('phishing.simulations.description')}
          </StyledDescription>
          <DemoContainer>
            {!isPhishingSenderWhitelisted && (
              <WhitelistingBanner
                id="start-whitelisting"
                onStartWhitelisting={
                  isDemoAccount ? undefined : handleStartWhitelisting
                }
                className="mb-4"
              />
            )}

            <InfoCards autoPhishingSummary={autoPhishingSummary} />

            {isAutoScheduleLoading && (
              <AutoScheduleLoadingPanel>
                <CourseProcessing
                  title={t(
                    autoScheduleOperation === 'disable'
                      ? 'phishing.autoSchedule.loading.disableTitle'
                      : 'phishing.autoSchedule.loading.title',
                  )}
                  description={t(
                    autoScheduleOperation === 'disable'
                      ? 'phishing.autoSchedule.loading.disableDescription'
                      : 'phishing.autoSchedule.loading.description',
                  )}
                />
              </AutoScheduleLoadingPanel>
            )}

            <AutoScheduleSection $hidden={isAutoScheduleLoading}>
              <SchedulerWrapper>
                {hasAutoSchedulerFlag && (
                  <AutoScheduleHeader
                    currentAccount={currentAccount as Account}
                    isDemoAccount={isDemoAccount}
                    onLoadingChange={(loading, operation) => {
                      setIsAutoScheduleLoading(loading);
                      setAutoScheduleOperation(operation);
                    }}
                  />
                )}
                <StyledScheduler>
                  <SimulationScheduler
                    className="simulation-scheduler"
                    simulations={campaingsMapped ?? []}
                    showLanguage={isTemplateLevelView}
                    deletingSimulationId={deletingCampaignId}
                    onSimulationChange={
                      isDemoAccount || isCampaignEditRestricted
                        ? undefined
                        : handleCampaignChange
                    }
                    onSimulationDelete={
                      isDemoAccount || isCampaignEditRestricted
                        ? undefined
                        : handleCampaignDelete
                    }
                    onSimulationEdit={
                      isDemoAccount || isCampaignEditRestricted
                        ? undefined
                        : handleCampaignEdit
                    }
                    onSimulationSendTestEmail={
                      isDemoAccount || isCampaignEditRestricted
                        ? undefined
                        : (sim) => setTestEmailSimulation(sim)
                    }
                  />
                </StyledScheduler>
              </SchedulerWrapper>

              <AddSimulationsCard
                disabledPlanWithAssistance={!!campaingsMapped?.length}
                hidePlanWithAssistance={isAutoScheduleEnabled}
                onSingleSimulation={handleSingleSimulation}
                onPlanWithAssistance={handlePlanWithAssistance}
              />
            </AutoScheduleSection>

            <Actions>
              <div id="schedule-simulations" style={{ flexShrink: 0 }}>
                <AppButton
                  isDisabled={
                    !isPhishingSenderWhitelisted ||
                    hasDateMissingDraftCampaigns ||
                    campaingsMapped.every(
                      (campaign) =>
                        campaign.status !== CampaignStatusesEnum.DRAFT,
                    )
                  }
                  iconPos="right"
                  onClick={
                    isDemoAccount ? undefined : handleScheduleSimulations
                  }
                >
                  <span className="mr-2">
                    {t('phishing.simulations.scheduleButton')}
                  </span>
                </AppButton>
              </div>
              {campaingsMapped.some(
                (campaign) => campaign.status === CampaignStatusesEnum.DRAFT,
              ) && (
                <FlexContainer className="info-message" justify="flex-start">
                  <i className="pi pi-info-circle"></i>
                  <p
                    dangerouslySetInnerHTML={{
                      __html: t(
                        'phishing.simulations.unscheduledWarning.message',
                        {
                          count: campaingsMapped?.filter(
                            (campaign) =>
                              campaign.status === CampaignStatusesEnum.DRAFT,
                          ).length,
                        },
                      ),
                    }}
                  />
                </FlexContainer>
              )}
              {(!isPhishingSenderWhitelisted ||
                hasDateMissingDraftCampaigns) && (
                <Tooltip
                  target="#schedule-simulations"
                  content={
                    !isPhishingSenderWhitelisted
                      ? t('phishing.simulations.scheduleButtonTooltip')
                      : t(
                          'phishing.simulations.scheduleButtonMissingDateTooltip',
                        )
                  }
                  position="top"
                />
              )}
            </Actions>
          </DemoContainer>

          {(isGeneratingSchedule || isLaunchingSimulations) && (
            <LoadingOverlay className="loading-overlay">
              <div className="loading-content">
                <ProgressSpinner />
                <div>
                  <p className="loading-text">
                    {isGeneratingSchedule
                      ? t('phishing.simulations.loading.generating')
                      : t('phishing.simulations.loading.scheduling')}
                    <span className="loading-dots">
                      <span>.</span>
                      <span>.</span>
                      <span>.</span>
                    </span>
                  </p>
                  {isGeneratingSchedule && (
                    <p className="loading-subtext">
                      {t('phishing.simulations.loading.takeMinutes')}
                    </p>
                  )}
                </div>
              </div>
            </LoadingOverlay>
          )}
        </>
      )}
      <PlanSimulationModal
        selectedTask={selectedTask}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onPlanSimulation={handlePlanSimulation}
      />
      {currentAccount?.id && (
        <WhitelistingModal
          isOpen={isWhitelistingModalOpen}
          onClose={handleCloseWhitelistingModal}
          accountId={currentAccount.id}
        />
      )}

      <PlanWithAssistanceModal
        isOpen={isPlanWithAssistanceModalOpen}
        onClose={handleClosePlanWithAssistanceModal}
        onCreateSchedule={handleCreateSchedule}
      />
      <ScheduleUsersWarningModal
        isOpen={isUsersWarningModalOpen}
        onClose={() => setIsUsersWarningModalOpen(false)}
        onScheduleAnyway={() => {
          setIsUsersWarningModalOpen(false);
          launchSimulations();
        }}
        activeUsersCount={activeUsersCount}
      />
      <SendTestEmailDialog
        visible={
          !!testEmailSimulation?.emailTemplateId &&
          !!testEmailSimulation?.sendingProfileId &&
          !!testEmailSimulation?.landingPageId
        }
        onHide={() => setTestEmailSimulation(null)}
        campaignId={
          testEmailSimulation?.testSendId ?? testEmailSimulation?.id ?? ''
        }
        emailTemplateId={testEmailSimulation?.emailTemplateId ?? ''}
        sendingProfileId={testEmailSimulation?.sendingProfileId ?? ''}
        landingPageId={testEmailSimulation?.landingPageId ?? ''}
        testSends={testEmailSimulation?.testSends}
      />
    </FlexContainer>
  );
};
